import { useApolloClient } from "@apollo/client";
import { useCallback, useMemo, useRef, useState } from "react";
import { Platform, View } from "react-native";
import CreateGatheringAddonDocument from "src/api/graphql/__generated__/documents/CreateGatheringAddonDocument";
import EditGatheringAddonDocument from "src/api/graphql/__generated__/documents/EditGatheringAddonDocument";
import GetTitleFromUrlDocument from "src/api/graphql/__generated__/documents/GetTitleFromUrlDocument";
import AddonVariant from "src/api/graphql/__generated__/enums/AddonVariant";
import linkSrc from "src/assets/images/addons/link.png";
import locationSrc from "src/assets/images/addons/location.png";
import spotifySrc from "src/assets/images/addons/spotify.png";
import venmoSrc from "src/assets/images/addons/venmo.png";
import LabeledTextInput from "src/components/LabeledTextInput";
import PillButton from "src/components/PillButton";
import { ADDON_TITLE_CHAR_LIMIT } from "src/helpers/addonHelpers/constants";
import parseVenmoHandleFromUrl from "src/helpers/addonHelpers/helpers/parseVenmoHandleFromUrl";
import log from "src/helpers/log";
import useDevice from "src/hooks/useDevice";
import useStyle from "src/hooks/useStyle";
import { useInsideModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import { paddingMedium, paddingSmall, paddingXL, paddingXXL } from "src/styles/spacing";
import { AnimatePresence, Image, Text } from "src/swsh-native";
import getCopyForGatheringAddonEditModal from "../helpers/getCopyForGatheringAddonEditModal";
import useDeleteGatheringAddon from "../hooks/useDeleteGatheringAddon";
import { EditAddonHorizontalPadding } from "../styles";

const GatheringTextAddonEdit = () => {
	const apolloClient = useApolloClient();
	const { device } = useDevice();
	const { theme } = useStyle();

	const {
		closeSelf,
		props: { variant, gatheringId, addonId, url: existingUrl, label: existingTitle },
	} = useInsideModal<GatheringModal["GatheringAddonEditModal"]>();

	const urlRef = useRef(existingUrl ?? null);
	const titleRef = useRef(existingTitle ?? null);
	const [fetchedTitle, setFetchedTitle] = useState<string | null>(null);
	const [exceededCharLimit, setExceededCharLimit] = useState(false);
	const [showTitleInput, setShowTitleInput] = useState(!!(existingTitle || existingUrl));

	const initialValueUrl = useMemo(
		() =>
			variant === AddonVariant.Venmo
				? (parseVenmoHandleFromUrl(urlRef.current) ?? undefined)
				: existingUrl,
		[variant, existingUrl],
	);
	const initialValueTitle = useMemo(
		() => existingTitle ?? fetchedTitle ?? undefined,
		[existingTitle, fetchedTitle],
	);

	const titleCharLimit = useMemo(() => {
		return variant === AddonVariant.Spotify ? 25 : ADDON_TITLE_CHAR_LIMIT;
	}, [variant]);

	const imageSrc = useMemo(() => {
		switch (variant) {
			case AddonVariant.Venmo:
				return venmoSrc;
			case AddonVariant.Location:
				return locationSrc;
			case AddonVariant.Spotify:
				return spotifySrc;
			case AddonVariant.Link:
			default:
				return linkSrc;
		}
	}, [variant]);

	const { header, label, placeholder } = useMemo(
		() => getCopyForGatheringAddonEditModal({ variant }),
		[variant],
	);

	const maybeFetchAndSetTitle = useCallback(
		async (url: string) => {
			if (titleRef.current && titleRef.current.length > 0) return;
			try {
				const {
					data: { getTitleFromURL },
				} = await apolloClient.query({
					query: GetTitleFromUrlDocument,
					fetchPolicy: "cache-first",
					variables: {
						input: { url },
					},
				});
				const fetchedTitle = getTitleFromURL?.title;
				if (fetchedTitle && (!titleRef.current || titleRef.current.length < 1)) {
					setFetchedTitle(fetchedTitle);
					if (fetchedTitle.length > titleCharLimit) {
						setExceededCharLimit(true);
					} else {
						setExceededCharLimit(false);
					}
				}
			} catch (err) {
				log.error({ err }, "Error fetching title");
			}
		},
		[apolloClient, titleCharLimit],
	);

	const handleDebouncedChangeText = useCallback(
		async (text: string) => {
			setShowTitleInput(true);
			if (urlRef.current === null || text.length - urlRef.current.length > 2) {
				maybeFetchAndSetTitle(text);
			}
		},
		[maybeFetchAndSetTitle],
	);
	const deleteGatheringAddon = useDeleteGatheringAddon();

	const handleSave = useCallback(async () => {
		const url =
			variant === AddonVariant.Venmo &&
			urlRef.current &&
			!urlRef.current.includes("venmo.com")
				? `https://account.venmo.com/u/${urlRef.current.replace("@", "")}`
				: urlRef.current;
		const title = titleRef.current ?? fetchedTitle;
		if (addonId) {
			await apolloClient.mutate({
				mutation: EditGatheringAddonDocument,
				variables: {
					input: {
						addonId,
						gatheringId,
						url,
						label: title,
					},
				},
			});
		} else {
			await apolloClient.mutate({
				mutation: CreateGatheringAddonDocument,
				variables: {
					input: {
						gatheringId,
						variant,
						url,
						label: title,
					},
				},
			});
		}
		closeSelf();
	}, [addonId, variant, fetchedTitle, closeSelf, apolloClient, gatheringId]);

	return (
		<View
			style={{
				rowGap: device === "mobile" ? 12 : 56,
				paddingHorizontal: EditAddonHorizontalPadding,
			}}
		>
			<View
				style={{
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					paddingVertical: paddingSmall,
				}}
			>
				<View
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						alignItems: "center",
						rowGap: paddingSmall,
					}}
				>
					<Text testID="EditAddonHeader" basic semibold>
						{header}
					</Text>
				</View>
				{existingTitle || existingUrl ? (
					<PillButton
						testID="EditAddonRemove"
						onPress={deleteGatheringAddon}
						textProps={{ xs: true, semibold: true }}
						colorVariant="Destructive"
					>
						Delete
					</PillButton>
				) : (
					<PillButton
						testID="EditAddonClose"
						onPress={closeSelf}
						textProps={{ xs: true, semibold: true }}
					>
						Cancel
					</PillButton>
				)}
				<PillButton
					testID="EditAddonSave"
					onPress={handleSave}
					textProps={{ xs: true, semibold: true }}
					disabled={exceededCharLimit}
				>
					Save
				</PillButton>
			</View>

			<View style={{ alignItems: "center" }}>
				<View style={{ alignItems: "center" }}>
					<Image
						alt="Supplementary graphic for addon"
						src={imageSrc}
						dim={{
							// Keyboard height is higher on Android so we shrink the image to fit text inputs
							width:
								Platform.OS === "android" ? 140 : device === "mobile" ? 200 : 260,
							aspectRatio: 1,
						}}
					/>
				</View>
			</View>

			<View
				style={{
					paddingHorizontal: device === "mobile" ? paddingMedium : paddingXL,
					rowGap: device === "mobile" ? 12 : paddingXXL,
				}}
			>
				<LabeledTextInput
					testID="EditAddonLinkInput"
					sizeVariant={device === "mobile" ? "Small" : "Medium"}
					label={label}
					placeholder={placeholder}
					onChangeText={(text) => {
						urlRef.current = text;
					}}
					onDebouncedChangeText={handleDebouncedChangeText}
					autoFocus
					initialValue={initialValueUrl}
					onBlur={(e) => {
						maybeFetchAndSetTitle(e.nativeEvent.text);
					}}
					spellCheck={variant !== AddonVariant.Venmo}
				/>

				{showTitleInput && (
					<AnimatePresence>
						<LabeledTextInput
							testID="EditAddonTitleInput"
							sizeVariant={device === "mobile" ? "Small" : "Medium"}
							key={`input-${fetchedTitle}`} // render new input on fetched title change to get a new initialValue
							label="TITLE"
							placeholder="Add custom title"
							onChangeText={(text) => {
								titleRef.current = text;
							}}
							onDebouncedChangeText={(text) => {
								if (text.length > titleCharLimit) {
									setExceededCharLimit(true);
								} else {
									setExceededCharLimit(false);
								}
							}}
							initialValue={initialValueTitle}
							showCharacterLimit={true}
							characterLimit={titleCharLimit}
							showClear={true}
							autoCorrect={false}
						/>
					</AnimatePresence>
				)}
			</View>
		</View>
	);
};
export default GatheringTextAddonEdit;
